import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Alert, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Customer, CreateCustomerRequest, UpdateCustomerRequest, SearchParams } from '../types';
import { CustomerList, CustomerDialog, DeleteCustomerDialog, BulkUploadDialog } from '../features/customers';
import { CustomerListRef } from '../features/customers';
import { useLanguage } from '../contexts/LanguageContext';
import { useCustomers } from '../hooks/useCustomers';
import { useCustomerMutations } from '../hooks/useCustomerMutations';
import { errorMessage } from '../utils/errorMessage';
import { formatCustomerError } from '../utils/customerErrorMessage';
import CompanyScopeBanner from '../components/common/CompanyScopeBanner';

const Customers: React.FC = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const customerListRef = useRef<CustomerListRef>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 0,
    size: 10,
    sort: 'firstName',
    direction: 'asc',
    companyId: new URLSearchParams(location.search).get('companyId') || undefined,
  });

  const customersQuery = useCustomers(searchParams);
  const { saveCustomerMutation, deleteCustomerMutation } = useCustomerMutations();
  const shouldFocusSearch = new URLSearchParams(location.search).get('focus') === 'search';

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchParams.companyId) next.set('companyId', searchParams.companyId);
    if (searchParams.name) next.set('name', searchParams.name);
    if (searchParams.page !== undefined) next.set('page', String(searchParams.page));
    if (searchParams.size !== undefined) next.set('size', String(searchParams.size));
    if (searchParams.sort) next.set('sort', searchParams.sort);
    if (searchParams.direction) next.set('direction', searchParams.direction);
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
  }, [location.pathname, location.search, navigate, searchParams]);

  // Dialog states
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  }, []); 

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    if (customersQuery.error) {
      showNotification(customersQuery.error, 'error');
    }
  }, [customersQuery.error, showNotification]);

  const handleSearch = useCallback((name?: string) => {
    setSearchParams(prev => ({
      ...prev,
      name,
      page: 0
    }));
  }, []);

  const handlePaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    // This check is now robust because the ref is updated atomically with the state.
    if (customersQuery.isLoading) {
      return;
    }
    setSearchParams(prev => ({
      ...prev,
      page: prev.size !== model.pageSize ? 0 : model.page,
      size: model.pageSize,
    }));
  }, [customersQuery.isLoading]);

  const handleSortChange = useCallback((field?: string, direction?: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sort: field, direction, page: 0 }));
  }, []);

  const handleCompanyFilter = useCallback((companyId: string) => {
    setSearchParams(prev => ({
      ...prev,
      companyId: companyId || undefined,
      page: 0
    }));
  }, []);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('action') === 'add') {
      handleAddCustomer();
      const next = new URLSearchParams(location.search);
      next.delete('action');
      next.delete('focus');
      navigate({ pathname: '/customers', search: next.toString() ? `?${next}` : '' }, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!shouldFocusSearch) return;
    const timer = setTimeout(() => {
      customerListRef.current?.focusSearch();
    }, 100);
    return () => clearTimeout(timer);
  }, [shouldFocusSearch]);

  const handleAddCustomer = () => {
    setSelectedCustomer(null);
    setCustomerDialogOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerDialogOpen(true);
  };

  const handleDeleteCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const handleBulkUpload = () => {
    setBulkUploadDialogOpen(true);
  };

  const handleBulkUploadSuccess = () => {
    customersQuery.reload();
    showNotification(t('bulkUpload.uploadComplete'));
  };

  const handleSubmitCustomer = async (data: CreateCustomerRequest | UpdateCustomerRequest) => {
    try {
      await saveCustomerMutation.mutateAsync({ id: selectedCustomer?.id, data });
      if (selectedCustomer) {
        showNotification(t('customers.updated'));
      } else {
        showNotification(t('customers.created'));
      }
      setCustomerDialogOpen(false);
    } catch (error: unknown) {
      showNotification(formatCustomerError(error, t('customers.errors.saveFailed'), t), 'error');
      throw error;
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCustomer) return;
    try {
      await deleteCustomerMutation.mutateAsync(selectedCustomer.id);
      showNotification(t('customers.deleted'));
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    } catch (error: unknown) {
      showNotification(errorMessage(error, 'Failed to delete customer'), 'error');
    }
  };

  const handleCloseDialog = () => {
    setCustomerDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleCloseBulkUploadDialog = () => {
    setBulkUploadDialogOpen(false);
  };

  const handleCustomerClick = (customerId: string, customerName: string) => {
    navigate(`/customers/${customerId}`, { 
      state: { customerName, returnTo: '/customers', companyId: searchParams.companyId }
    });
  };

  return (
    <Container maxWidth={false} disableGutters>
      <CompanyScopeBanner companyId={searchParams.companyId} />
      <CustomerList
        ref={customerListRef}
        paginatedData={customersQuery.data}
        loading={customersQuery.isLoading}
        searchParams={searchParams}
        onSearch={handleSearch}
        onPaginationModelChange={handlePaginationModelChange}
        onSortChange={handleSortChange}
        onEdit={handleEditCustomer}
        onDelete={handleDeleteCustomer}
        onAdd={handleAddCustomer}
        onBulkUpload={handleBulkUpload}
        onCustomerClick={handleCustomerClick}
        onCompanyFilter={handleCompanyFilter}
        autoFocusSearch={shouldFocusSearch}
      />

      <CustomerDialog
        open={customerDialogOpen}
        customer={selectedCustomer}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitCustomer}
        isLoading={saveCustomerMutation.isPending}
      />

      <DeleteCustomerDialog
        open={deleteDialogOpen}
        customer={selectedCustomer}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        isLoading={deleteCustomerMutation.isPending}
      />

      <BulkUploadDialog
        open={bulkUploadDialogOpen}
        onClose={handleCloseBulkUploadDialog}
        onSuccess={handleBulkUploadSuccess}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Customers;
